import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import {
  Ambiguity,
  ExecutionPlan,
  ProposedResolution,
  StepPlan,
} from '../../../../shared/templateGen/templateInterfaces';

export interface PlanApprovalEvent {
  approved_resolutions: ProposedResolution[];
  step_overrides: Record<string, string>;
}

export interface RewriteProposal {
  id: string;           // "A", "B", "C"
  source_type: string;  // "KB" or "assumption"
  source_file?: string; // filename if KB-based
  rewrite: string;      // the proposed rewrite text
}

export interface ClarificationCard {
  ambiguity: any;       // {id, question, impact, priority, category, source}
  kb_result: any | null; // {file_name, evidence_quote, confidence, search_score, proposed_text}
  proposals: RewriteProposal[]; // rewrite proposals for the source phrase
  evolved_story?: string; // story text AFTER this step's rewrite
  user_action: 'pending' | 'approved' | 'modified' | 'rejected' | 'ignored';
  user_text: string;
}

export interface UserResponseEvent {
  ambiguity_id: string;
  action: string;
  text?: string;
  chosen_proposal?: RewriteProposal;
}

@Component({
  selector: 'app-execution-plan',
  templateUrl: './execution-plan.component.html',
  styleUrls: ['./execution-plan.component.scss'],
})
export class ExecutionPlanComponent implements OnInit, OnChanges {
  constructor(private sanitizer: DomSanitizer) {}

  // --- Mode ---
  @Input() mode: 'full' | 'conversation' = 'full';

  // --- Full mode inputs ---
  @Input() plan: ExecutionPlan | null = null;
  @Input() ambiguities: Ambiguity[] = [];

  // --- Conversation mode inputs ---
  @Input() currentQuestion: ClarificationCard | null = null;
  @Input() isThinking = false;
  @Input() answeredCards: ClarificationCard[] = [];
  @Input() complexityEstimate: any = null; // {complexity, reasoning, expected_questions_range}

  // --- Outputs ---
  @Output() approve = new EventEmitter<PlanApprovalEvent>();
  @Output() forceFullFlow = new EventEmitter<void>();
  @Output() userResponse = new EventEmitter<UserResponseEvent>();
  @Output() proceed = new EventEmitter<void>();
  @Output() openDocument = new EventEmitter<{file_name: string, evidence_quote: string}>();
  @Output() searchInDocument = new EventEmitter<{file_names: string[], ambiguity_id: string, question: string}>();

  // --- KB file picker state ---
  @Input() kbFiles: {name: string}[] = [];
  showFilePicker = false;
  selectedPickerFiles: Set<string> = new Set();
  pickerSearching = false;

  // --- Upload state ---
  @Output() refineProposal = new EventEmitter<{ambiguity_id: string, instructions: string}>();
  @Output() storyVersionSelected = new EventEmitter<{version: string, storyText: string}>();
  @Input() originalStory = '';
  @Input() finalStory = '';
  @Output() uploadAndSearch = new EventEmitter<{file: File, ambiguity_id: string, question: string}>();
  @Input() uploadStatus: 'idle' | 'uploading' | 'indexing' | 'searching' | 'done' | 'error' = 'idle';
  showUploadPanel = false;
  uploadFile: File | null = null;

  private allowedExtensions = ['.docx', '.txt', '.html', '.pdf', '.md', '.csv', '.json'];

  // Derived state (full mode)
  resolutions: ProposedResolution[] = [];
  unresolvedAmbiguities: Ambiguity[] = [];
  steps: StepPlan[] = [];

  // UI state (full mode)
  editingResolutionId: string | null = null;
  editText = '';

  // UI state (conversation mode)
  showAnsweredDrawer = false;
  sourceExpanded = false;
  cardAnimationClass = '';
  showProposalWriteOwn = false;
  proposalWriteOwnText = '';
  showProposalHints = false;
  proposalHintsText = '';
  showConfirmation = false;
  pendingResponse: UserResponseEvent | null = null;
  confirmationSource = '';
  confirmationRewrite = '';

  // Ignore confirmation state
  showIgnoreConfirm = false;

  // Inline source preview state
  sourcePreviewRewrite: string | null = null;

  // Collapsible sections state
  sourceCollapsed = false;
  impactCollapsed = true;
  kbCollapsed = true;

  // Markdown rendering cache
  private _markdownCache = new Map<string, SafeHtml>();

  // Proposal diff cache
  private _proposalDiffCache = new Map<string, {text: string, type: 'unchanged' | 'added' | 'removed'}[]>();

  // Timeline state
  selectedTimelineStep: number | null = null;
  storyViewerMode: 'evolved' | 'original' = 'evolved';
  storyDiffSegments: {text: string, type: 'unchanged' | 'added' | 'removed'}[] = [];
  showStepDetailModal = false;
  stepDetailCard: ClarificationCard | null = null;
  selectedStoryVersion: 'final' | 'original' | number = 'final';

  ngOnInit(): void {
    this._syncFromPlan();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.plan) {
      this._syncFromPlan();
    }
    if (changes.currentQuestion && this.mode === 'conversation') {
      this._resetConversationUI();
      // Trigger card entry animation
      this.cardAnimationClass = '';
      setTimeout(() => this.cardAnimationClass = 'card-enter', 10);
    }
  }

  // --- Conversation mode actions ---

  ignoreQuestion(): void {
    if (!this.currentQuestion) return;
    this.showIgnoreConfirm = false;
    this.currentQuestion.user_action = 'ignored';
    this.userResponse.emit({
      ambiguity_id: this.currentQuestion.ambiguity?.id || '',
      action: 'ignored',
    });
  }

  requestIgnore(): void {
    this.showIgnoreConfirm = true;
  }

  cancelIgnore(): void {
    this.showIgnoreConfirm = false;
  }

  // --- Markdown rendering ---

  renderMarkdown(text: string): SafeHtml {
    if (!text) return '';
    const cached = this._markdownCache.get(text);
    if (cached) return cached;
    const html = marked.parse(text, { async: false }) as string;
    const safe = this.sanitizer.bypassSecurityTrustHtml(html);
    this._markdownCache.set(text, safe);
    return safe;
  }

  // --- Proposal diff ---

  getProposalDiffSegments(proposal: RewriteProposal): {text: string, type: 'unchanged' | 'added' | 'removed'}[] {
    const cacheKey = proposal.id + ':' + proposal.rewrite;
    const cached = this._proposalDiffCache.get(cacheKey);
    if (cached) return cached;

    const source = this.currentQuestion?.ambiguity?.source || '';
    if (!source || !proposal.rewrite) {
      const result = [{text: proposal.rewrite || '', type: 'unchanged' as const}];
      this._proposalDiffCache.set(cacheKey, result);
      return result;
    }

    const segments = this._wordDiff(source, proposal.rewrite);
    this._proposalDiffCache.set(cacheKey, segments);
    return segments;
  }

  private _wordDiff(oldText: string, newText: string): {text: string, type: 'unchanged' | 'added' | 'removed'}[] {
    const oldWords = oldText.split(/(\s+)/);
    const newWords = newText.split(/(\s+)/);
    const oldSet = new Set(oldWords.filter(w => w.trim()));
    const newSet = new Set(newWords.filter(w => w.trim()));

    const segments: {text: string, type: 'unchanged' | 'added' | 'removed'}[] = [];
    for (const word of newWords) {
      if (!word.trim()) {
        segments.push({text: word, type: 'unchanged'});
      } else if (oldSet.has(word)) {
        segments.push({text: word, type: 'unchanged'});
      } else {
        segments.push({text: word, type: 'added'});
      }
    }
    return segments;
  }

  // --- Proposal actions ---

  chooseProposal(proposal: RewriteProposal): void {
    if (!this.currentQuestion) return;
    this.sourceCollapsed = false;
    this.sourcePreviewRewrite = proposal.rewrite;
    this.pendingResponse = {
      ambiguity_id: this.currentQuestion.ambiguity?.id || '',
      action: 'approved',
      text: proposal.rewrite,
      chosen_proposal: proposal,
    };
    this.showConfirmation = true;
  }

  startProposalWriteOwn(): void {
    this.showProposalWriteOwn = true;
    this.proposalWriteOwnText = this.currentQuestion?.ambiguity?.source || '';
  }

  submitProposalWriteOwn(): void {
    if (!this.currentQuestion) return;
    this.sourceCollapsed = false;
    this.sourcePreviewRewrite = this.proposalWriteOwnText;
    this.pendingResponse = {
      ambiguity_id: this.currentQuestion.ambiguity?.id || '',
      action: 'modified',
      text: this.proposalWriteOwnText,
    };
    this.showConfirmation = true;
    this.showProposalWriteOwn = false;
  }

  startProposalHints(): void {
    this.showProposalHints = true;
    this.proposalHintsText = '';
  }

  submitProposalHints(): void {
    if (!this.currentQuestion) return;
    this.refineProposal.emit({
      ambiguity_id: this.currentQuestion.ambiguity?.id || '',
      instructions: this.proposalHintsText,
    });
    this.showProposalHints = false;
    this.proposalHintsText = '';
  }

  // --- Confirmation step ---

  private _showConfirmation(source: string, rewrite: string, response: UserResponseEvent): void {
    this.confirmationSource = source;
    this.confirmationRewrite = rewrite;
    this.pendingResponse = response;
    this.showConfirmation = true;
  }

  confirmAction(): void {
    if (!this.pendingResponse || !this.currentQuestion) return;
    this.currentQuestion.user_action = this.pendingResponse.action as any;
    this.currentQuestion.user_text = this.pendingResponse.text || '';
    this.userResponse.emit(this.pendingResponse);
    this.showConfirmation = false;
    this.pendingResponse = null;
    this.sourcePreviewRewrite = null;
  }

  cancelConfirmation(): void {
    this.showConfirmation = false;
    this.pendingResponse = null;
    this.sourcePreviewRewrite = null;
  }

  // --- Timeline methods ---

  selectTimelineStep(index: number): void {
    if (this.selectedTimelineStep === index) {
      this.selectedTimelineStep = null;
      this.storyDiffSegments = [];
      return;
    }
    this.selectedTimelineStep = index;
    this.storyViewerMode = 'evolved';
    this._computeDiff(index);
  }

  openStepDetail(index: number, event: Event): void {
    event.stopPropagation();
    this.stepDetailCard = this.answeredCards[index] || null;
    this.showStepDetailModal = true;
  }

  closeStepDetail(): void {
    this.showStepDetailModal = false;
    this.stepDetailCard = null;
  }

  getActionLabel(action: string): string {
    switch (action) {
      case 'approved': return 'Approvata';
      case 'modified': return 'Modificata';
      case 'ignored': return 'Ignorata';
      case 'rejected': return 'Rifiutata';
      default: return action;
    }
  }

  getActionBadgeClass(action: string): string {
    switch (action) {
      case 'approved': return 'badge-success';
      case 'modified': return 'badge-warning';
      case 'ignored': return 'badge-secondary';
      case 'rejected': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  onStoryVersionChange(): void {
    // ngModel returns strings for radio values — coerce numeric indices
    const version = this.selectedStoryVersion;
    const numVersion = typeof version === 'string' && !isNaN(Number(version))
      ? Number(version) : version;

    let storyText = '';
    if (numVersion === 'original') {
      storyText = this.originalStory;
    } else if (numVersion === 'final') {
      storyText = this.finalStory || this.originalStory;
    } else if (typeof numVersion === 'number') {
      // For step N, we need the story AFTER step N's rewrite.
      // card.evolved_story is the story BEFORE the step. Use the next card's evolved_story.
      const nextCard = this.answeredCards[numVersion + 1];
      storyText = nextCard?.evolved_story || this.answeredCards[numVersion]?.evolved_story || this.originalStory;
    } else {
      storyText = this.originalStory;
    }
    this.storyVersionSelected.emit({
      version: String(this.selectedStoryVersion),
      storyText,
    });
  }

  private _computeDiff(index: number): void {
    const card = this.answeredCards[index];
    // The card's evolved_story is the story BEFORE this step's action.
    // Use source_phrase + user_text for a targeted diff.
    const storyBase = card?.evolved_story || this.originalStory;
    const sourcePhrase = card?.ambiguity?.source || '';
    const rewriteText = card?.user_text || '';

    // For ignored questions or no source, show story unchanged
    if (card?.user_action === 'ignored' || !sourcePhrase || !rewriteText) {
      this.storyDiffSegments = [{text: storyBase, type: 'unchanged'}];
      return;
    }

    // Targeted diff: find source_phrase in the story and show the replacement
    const idx = storyBase.indexOf(sourcePhrase);
    if (idx >= 0) {
      const segments: {text: string, type: 'unchanged' | 'added' | 'removed'}[] = [];
      if (idx > 0) {
        segments.push({text: storyBase.substring(0, idx), type: 'unchanged'});
      }
      segments.push({text: sourcePhrase, type: 'removed'});
      segments.push({text: rewriteText, type: 'added'});
      const after = storyBase.substring(idx + sourcePhrase.length);
      if (after) {
        segments.push({text: after, type: 'unchanged'});
      }
      this.storyDiffSegments = segments;
      return;
    }

    // Fallback: case-insensitive search
    const idxLower = storyBase.toLowerCase().indexOf(sourcePhrase.toLowerCase());
    if (idxLower >= 0) {
      const segments: {text: string, type: 'unchanged' | 'added' | 'removed'}[] = [];
      if (idxLower > 0) {
        segments.push({text: storyBase.substring(0, idxLower), type: 'unchanged'});
      }
      segments.push({text: storyBase.substring(idxLower, idxLower + sourcePhrase.length), type: 'removed'});
      segments.push({text: rewriteText, type: 'added'});
      const after = storyBase.substring(idxLower + sourcePhrase.length);
      if (after) {
        segments.push({text: after, type: 'unchanged'});
      }
      this.storyDiffSegments = segments;
      return;
    }

    // Could not locate source — show story with rewrite appended as highlight
    this.storyDiffSegments = [
      {text: storyBase, type: 'unchanged'},
      {text: ' ' + rewriteText, type: 'added'},
    ];
  }

  openFilePicker(): void {
    this.showFilePicker = true;
    this.selectedPickerFiles.clear();
    this.pickerSearching = false;
  }

  togglePickerFile(fileName: string): void {
    if (this.selectedPickerFiles.has(fileName)) {
      this.selectedPickerFiles.delete(fileName);
    } else {
      this.selectedPickerFiles.add(fileName);
    }
  }

  confirmPickerSearch(): void {
    if (this.selectedPickerFiles.size === 0 || !this.currentQuestion) return;
    this.pickerSearching = true;
    // Emit each selected file — parent will search in all and pick the best
    this.searchInDocument.emit({
      file_names: Array.from(this.selectedPickerFiles),
      ambiguity_id: this.currentQuestion.ambiguity?.id || '',
      question: this.currentQuestion.ambiguity?.question || '',
    });
  }

  /** Called by parent when search completes. */
  onPickerSearchDone(): void {
    this.pickerSearching = false;
    this.showFilePicker = false;
  }

  onUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!this.allowedExtensions.includes(ext)) {
      alert(`Formato non supportato. Usa: ${this.allowedExtensions.join(', ')}`);
      return;
    }
    this.uploadFile = file;
  }

  triggerUpload(): void {
    if (!this.uploadFile || !this.currentQuestion) return;
    this.uploadStatus = 'uploading';
    this.uploadAndSearch.emit({
      file: this.uploadFile,
      ambiguity_id: this.currentQuestion.ambiguity?.id || '',
      question: this.currentQuestion.ambiguity?.question || '',
    });
  }

  openKbDocument(kbResult: any): void {
    if (kbResult?.file_name) {
      this.openDocument.emit({
        file_name: kbResult.file_name,
        evidence_quote: kbResult.evidence_quote || '',
      });
    }
  }

  proceedWithTests(): void {
    this.proceed.emit();
  }

  getPriorityClass(priority: string): string {
    switch (priority?.toLowerCase()) {
      case 'high': return 'badge-danger';
      case 'medium': return 'badge-warning';
      case 'low': return 'badge-success';
      default: return 'badge-secondary';
    }
  }

  private _resetConversationUI(): void {
    this.showFilePicker = false;
    this.selectedPickerFiles.clear();
    this.pickerSearching = false;
    this.showUploadPanel = false;
    this.uploadFile = null;
    this.uploadStatus = 'idle';
    this.showProposalWriteOwn = false;
    this.proposalWriteOwnText = '';
    this.showProposalHints = false;
    this.proposalHintsText = '';
    this.showConfirmation = false;
    this.pendingResponse = null;
    this.confirmationSource = '';
    this.confirmationRewrite = '';
    this.selectedTimelineStep = null;
    this.storyDiffSegments = [];
    this.showStepDetailModal = false;
    this.selectedPickerFiles.clear();
    this.showIgnoreConfirm = false;
    this.sourceCollapsed = false;
    this.impactCollapsed = true;
    this.kbCollapsed = true;
    this.sourcePreviewRewrite = null;
    this._markdownCache.clear();
    this._proposalDiffCache.clear();
  }

  // --- Resolution actions (full mode) ---

  approveResolution(r: ProposedResolution): void {
    r.user_action = 'approved';
  }

  startEditResolution(r: ProposedResolution): void {
    this.editingResolutionId = r.ambiguity_id;
    this.editText = r.proposed_text;
  }

  saveEditResolution(r: ProposedResolution): void {
    r.user_action = 'modified';
    r.user_modified_text = this.editText;
    this.editingResolutionId = null;
    this.editText = '';
  }

  cancelEdit(): void {
    this.editingResolutionId = null;
    this.editText = '';
  }

  rejectResolution(r: ProposedResolution): void {
    r.user_action = 'rejected';
  }

  // --- Step overrides ---

  toggleStep(step: StepPlan): void {
    if (step.status === 'skipped') {
      step.user_override = step.user_override === 'force_include' ? undefined : 'force_include';
    } else if (step.status === 'pending') {
      step.user_override = step.user_override === 'force_skip' ? undefined : 'force_skip';
    }
  }

  getStepIcon(step: StepPlan): string {
    if (step.status === 'completed') return 'ft-check-circle';
    if (step.user_override === 'force_include') return 'ft-plus-circle';
    if (step.user_override === 'force_skip') return 'ft-minus-circle';
    if (step.status === 'skipped') return 'ft-skip-forward';
    return 'ft-circle';
  }

  getStepClass(step: StepPlan): string {
    if (step.status === 'completed') return 'step-completed';
    if (step.user_override === 'force_include') return 'step-forced-include';
    if (step.user_override === 'force_skip') return 'step-forced-skip';
    if (step.status === 'skipped') return 'step-skipped';
    return 'step-pending';
  }

  // --- Confidence badge ---

  getConfidenceBadgeClass(confidence: string): string {
    switch (confidence) {
      case 'high': return 'badge-success';
      case 'medium': return 'badge-warning';
      case 'low': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  getComplexityBadgeClass(): string {
    if (!this.plan) return 'badge-secondary';
    switch (this.plan.complexity) {
      case 'simple': return 'badge-success';
      case 'medium': return 'badge-warning';
      case 'complex': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  // --- Approval ---

  get allResolutionsActioned(): boolean {
    return this.resolutions.every(r => r.user_action && r.user_action !== 'pending');
  }

  onApprove(): void {
    const overrides: Record<string, string> = {};
    for (const step of this.steps) {
      if (step.user_override) {
        overrides[step.step_id] = step.user_override;
      }
    }
    this.approve.emit({
      approved_resolutions: this.resolutions,
      step_overrides: overrides,
    });
  }

  onForceFullFlow(): void {
    for (const step of this.steps) {
      if (step.status === 'skipped') {
        step.user_override = 'force_include';
      }
    }
    this.forceFullFlow.emit();
  }

  // --- Helpers ---

  getAmbiguityById(id: string): Ambiguity | undefined {
    return this.ambiguities.find(a => a.id === id);
  }

  private _syncFromPlan(): void {
    if (!this.plan) return;
    this.resolutions = (this.plan.proposed_resolutions || []).map(r => ({
      ...r,
      user_action: r.user_action || 'pending',
    }));
    this.unresolvedAmbiguities = (this.plan.unresolved_ambiguity_ids || [])
      .map(id => this.ambiguities.find(a => a.id === id))
      .filter((a): a is Ambiguity => !!a);
    this.steps = (this.plan.recommended_steps || []).map((s: string | {step_id?: string; name: string; status?: string; skip_reason?: string; user_override?: string}) => {
      if (typeof s === 'string') {
        return { step_id: s, name: s, status: 'pending' as const };
      }
      return {
        step_id: s.step_id || s.name,
        name: s.name,
        status: (s.status || 'pending') as 'completed' | 'pending' | 'skipped',
        skip_reason: s.skip_reason,
        user_override: s.user_override as 'force_include' | 'force_skip' | undefined,
      };
    });
  }
}
